import axios from 'axios';

import { getApiHost } from '@hotjar/common';
import {
  getUserResources,
  USER_RESOURCES_QUERY_CONFIG,
  USER_RESOURCES_QUERY_KEY,
} from '@hotjar/user-resources';
import { queryClient } from '@hotjar/webapp/utils/react-query';
import type { Organization, Invitation, OrganizationMemberBaseInfo } from './model';
import type {
  OrganizationAccess,
  OrganizationId,
  OrganizationMemberBaseInfo as OrganizationMemberBaseInfoDto,
} from './dto';
import { mapOrganizationUserDtoToModel } from './mapper';

type AccountId = number;
interface ApiBaseResponse {
  success: boolean;
  error?: string | Error;
}

interface OrganizationUsersResponse extends ApiBaseResponse {
  users?: OrganizationMemberBaseInfoDto[];
}

export const ORGANIZATIONS_QUERY_KEY = 'organizations';

export const ORGANIZATIONS_QUERY_CONFIG = {
  staleTime: 60 * 60 * 1000,
  cacheTime: 60 * 60 * 1000,
  refetchOnWindowFocus: false,
};

export const organizationSelector =
  (organizationId: number) =>
  (organizations: Array<Organization>): Organization => {
    const requestedOrganization = organizations.find(
      (organization) => organization.id === organizationId,
    );
    if (!requestedOrganization) {
      throw new Error('ERR_NOT_FOUND');
    }
    return requestedOrganization;
  };

type UserId = Parameters<typeof getUserResources>[0];

export function getOrganizations(userId: UserId): Promise<Array<Organization>> {
  return queryClient
    .fetchQuery(USER_RESOURCES_QUERY_KEY, () => getUserResources(userId), {
      ...USER_RESOURCES_QUERY_CONFIG,
    })
    .then(({ organizations }) => organizations);
}

export async function invite(
  accountId: AccountId,
  organizatinId: OrganizationId,
  invitations: Invitation[],
): Promise<ApiBaseResponse> {
  const response = await axios.post<ApiBaseResponse>(
    `${getApiHost()}/api/v1/accounts/${accountId}/organizations/${organizatinId}`,
    {
      action: 'invite',
      users: JSON.stringify(invitations),
    },
  );
  return response.data;
}

export async function updateOrganizationUserAccess(
  organizationId: OrganizationId,
  userId: UserId,
  access: OrganizationAccess,
): Promise<ApiBaseResponse> {
  const response = await axios.put<ApiBaseResponse>(
    `/api/v1/users/${userId}/organizations/${organizationId}`,
    {
      access,
    },
  );
  return response.data;
}

export async function revokeOrganizationUserAccess(
  organizationId: OrganizationId,
  userId: UserId,
): Promise<ApiBaseResponse> {
  const response = await axios.delete<ApiBaseResponse>(
    `/api/v1/users/${userId}/organizations/${organizationId}`,
  );
  return response.data;
}

/**
 * Gets the base info (name, id and picture) of members of an organization.
 *
 * @param organizationId    The organization id
 * @param usersIds  A list of users ids to retrieve the info
 */
export async function getOrganizationMembersBaseInfo(
  organizationId: OrganizationId | null,
  usersIds: Array<UserId> | null,
): Promise<OrganizationMemberBaseInfo[] | null> {
  const params =
    usersIds === null
      ? {}
      : usersIds
          // if we pass the ids as it is to axios, will compute to: ?user_id[]=x&user_id[]=y...
          // and BE will throw a 400 Bad Request.
          .reduce((acc, curr) => {
            acc.append('user_id', curr.toString());

            return acc;
          }, new URLSearchParams());

  const response = await axios.get<OrganizationUsersResponse>(
    `/api/v1/organizations/${organizationId}/users`,
    {
      params,
    },
  );

  const { success, users } = response.data;

  if (!success || !users || users.length === 0) {
    return null;
  }

  return users.map((user: OrganizationMemberBaseInfoDto) => mapOrganizationUserDtoToModel(user));
}
